import React, { useEffect } from 'react';
import { useFormikContext, getIn } from 'formik';

import {
  exhibitionOptionsPermi,
  permissionaryReportLayout,
  situacoesPermissionario,
  situacoesLinha
} from './permissionaries-report-options';

import { useStipReport } from '../../hooks/use-stip-report';
import { useFetchPolos } from '../../hooks/fetch/use-fetch-polos';
import { useFetchVehicleTypes } from '../../hooks/fetch/use-fetch-vehicle-types';
import { useFetchItineraryLocations } from '../../hooks/fetch/use-fetch-itinerary-locations';
import { useFetchFilteredPermissionariesByLine } from '../../hooks/fetch/use-fetch-filtered-permissionaries-by-line';

import Loading from '../../../../layouts/Loading';
import { GridItem } from '../../components/GridItem';
import { ReportSelect } from '../../components/ReportSelect';
import { ReportTextField } from '../../components/ReportTextField';
import { ReportNumberField } from "../../components/ReportNumberField";
import { ReportDatePicker } from '../../components/ReportDatePicker';
import { ReportMultiSelect } from '../../components/ReportMultiSelect';
import { ReportSectionTitle } from '../../components/ReportSectionTitle';
import { ReportTogglerGroup } from '../../components/ReportTogglerGroup';
import { ReportMultiAutocomplete } from '../../components/ReportMultiAutocomplete';
import {ReportRadioGroup} from "../../components/ReportRadioGroup";

const PERMISSIONARIO = 6;
const CEPE = 5;
let isValidatingDtVigencia = false;
let isValidatingDtVencimento = false;

const PermissionariesReport = ({ validateField }) => {
  const { handleSelectChange, handleToggleChange, handleRadioChange } = useStipReport();
  const { values, setFieldValue } = useFormikContext();

  const {
    loading: loadingPermissionaries,
    lines,
    permissionaries,
  } = useFetchFilteredPermissionariesByLine({
    lineRequest: { boSlic: true, boOrdenarPorNuLinha: true },
  });

  const { loading: loadingPolos, polos } = useFetchPolos();
  const { loading: loadingVehicles, vehicleTypes } = useFetchVehicleTypes();
  const { loading: loadingCities, locations: cities } = useFetchItineraryLocations();

  const loading = loadingPolos || loadingPermissionaries || loadingCities || loadingVehicles;
  const reportByCepe = values.cdTipoRelatorioLinhas === CEPE;
  const reportByPermissionary = values.cdTipoRelatorioLinhas === PERMISSIONARIO;
  const noPermissionaries = permissionaries.length === 0;

  const permissionariesFieldLabel = noPermissionaries
    ? 'Não há permissionários disponíveis'
    : 'Todos os permissionários';

  useEffect(() => {
    if (values.cdTipoRelatorioLinhas !== PERMISSIONARIO) {
      setFieldValue('boExibirPendencias', false);
    }
    // eslint-disable-next-line
  }, [values.cdTipoRelatorioLinhas]);

  if (loading) {
    return <Loading />;
  }

  return (
    <GridItem container spacing={3}>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Selecionar tipo de relatório:</ReportSectionTitle>
        <ReportSelect
          options={permissionaryReportLayout}
          name="cdTipoRelatorioLinhas"
          onSelect={handleSelectChange}
        />
      </GridItem>

      {reportByPermissionary && (
        <GridItem sm={6}>
          <ReportSectionTitle gutterBottom>Opções de permissionários:</ReportSectionTitle>
          <ReportTogglerGroup options={exhibitionOptionsPermi} onToggle={handleToggleChange} />
        </GridItem>
      )}

      <GridItem>
        <ReportSectionTitle divider/>
      </GridItem>
      <GridItem sm={6}>
        <ReportRadioGroup
          label="Situação do permissionário:"
          options={situacoesPermissionario}
          name={'situacaoPermissionario'}
          onSelect={handleRadioChange}
        />
      </GridItem>

      <GridItem sm={6}>
        <ReportRadioGroup
          label="Situação da linha:"
          options={situacoesLinha}
          name={'situacaoLinha'}
          onSelect={handleRadioChange}
        />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider>Opções de filtro:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Selecione o(s) polo(s):</ReportSectionTitle>
        <ReportMultiAutocomplete
          options={polos}
          onSelect={handleSelectChange}
          label="Todos os polos"
          name="polos"
          valueProperty="cdMunicipio"
          labelProperty="nmMunicipio"
        />
      </GridItem>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Selecione a(s) localidades(s):</ReportSectionTitle>
        <ReportMultiAutocomplete
          options={cities}
          onSelect={handleSelectChange}
          label="Todos as localidades"
          name="localidades"
          valueProperty="deNome"
          labelProperty="deNome"
        />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider />
      </GridItem>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Selecione a(s) linha(s) / serviço(s):</ReportSectionTitle>
        <ReportMultiAutocomplete
          options={lines}
          disabled={lines.length === 0}
          onSelect={handleSelectChange}
          label={lines.length === 0 ? 'Não há linhas disponíveis' : 'Todos as linhas / serviços'}
          name="linhas"
          valueProperty="cdLinha"
          labelProperty="deDescricao"
          limitTags={1}
        />
      </GridItem>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Selecione o(s) permissionário(s):</ReportSectionTitle>
        <ReportMultiAutocomplete
          options={permissionaries}
          disabled={noPermissionaries}
          onSelect={handleSelectChange}
          label={permissionariesFieldLabel}
          name="permissionarios"
          valueProperty="cdLinhaPermissionario"
          labelProperty="deNomePermissionario"
          uppercaseLabels
          limitTags={1}
        />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider />
      </GridItem>
      <GridItem sm={4}>
        <ReportSectionTitle gutterBottom>Termo de permissão:</ReportSectionTitle>
        <ReportTextField name="nuTermo" label="Número do termo de permissão" />
      </GridItem>

      { reportByCepe ?
        <>
          <GridItem sm={2}/>
          <GridItem sm={4}>
            <ReportSectionTitle gutterBottom>Placa:</ReportSectionTitle>
            <ReportTextField name="dePlaca" label="Número da placa" />
          </GridItem>
        </>

        : null
      }

      { reportByPermissionary ?
        <>
          <GridItem>
            <ReportSectionTitle divider />
          </GridItem>
          <GridItem sm={4}>
            <ReportSectionTitle gutterBottom>Número do CEPE:</ReportSectionTitle>
            <ReportNumberField name="nuCepe" label="Número do CEPE"
                               validate={(value) => {
                                 if (Boolean(value)) {
                                   let nuCepe = parseInt(value);
                                   if (nuCepe === 0 || nuCepe > 999_999_999) return "Número do CEPE inválido.";
                                 }
                               }}
            />
          </GridItem>

          <GridItem sm={2}/>

          <GridItem sm={4}>
            <ReportSectionTitle gutterBottom>Ano do CEPE:</ReportSectionTitle>
            <ReportNumberField name="anoCepe" label="Ano do CEPE"
                               validate={(value) => {
                                 if (Boolean(value)) {
                                   let anoCepe = parseInt(value);
                                   if (anoCepe === 0 || anoCepe > 9999) return "Ano do CEPE inválido.";
                                 }
                               }}
            />
          </GridItem>

          <GridItem>
            <ReportSectionTitle divider>Filtrar por data de início de vigência:</ReportSectionTitle>
          </GridItem>
          <GridItem sm={6}>
            <ReportDatePicker name="dtVigencia.dtInicio" label="Início"
                              validate={(value) => {
                                if (!isValidatingDtVigencia) {
                                  isValidatingDtVigencia = true;
                                  validateField("dtVigencia.dtFim");
                                }

                                let dtInicio = value;
                                if (Boolean(dtInicio)) {
                                  if (!dtInicio.isValid()) return "Favor informar uma data válida";

                                  let dtFim = getIn(values, "dtVigencia.dtFim");
                                  if (Boolean(dtFim)) {
                                    let rangeValido = dtInicio.isValid() && dtFim.isValid();
                                    if (rangeValido) {
                                      dtInicio = dtInicio.toDate();
                                      dtInicio.setHours(0, 0, 0, 0);
                                      dtFim = dtFim.toDate();
                                      dtFim.setHours(0, 0, 0, 0);
                                      if (dtInicio > dtFim) return "Não é possível informar uma data início maior que a data fim para o campo 'Data de início de vigência'";
                                    }
                                  }
                                }

                                isValidatingDtVigencia = false;
                              }}
            />
          </GridItem>
          <GridItem sm={6}>
            <ReportDatePicker name="dtVigencia.dtFim" label="Fim"
                              validate={(value) => {
                                if (!isValidatingDtVigencia) {
                                  isValidatingDtVigencia = true;
                                  validateField("dtVigencia.dtInicio");
                                }

                                let dtFim = value;
                                if (Boolean(dtFim)) {
                                  if (!dtFim.isValid()) return "Favor informar uma data válida";

                                  let dtInicio = getIn(values, "dtVigencia.dtInicio");
                                  if (Boolean(dtInicio)) {
                                    let rangeValido = dtInicio.isValid() && dtFim.isValid();
                                    if (rangeValido) {
                                      dtInicio = dtInicio.toDate();
                                      dtInicio.setHours(0, 0, 0, 0);
                                      dtFim = dtFim.toDate();
                                      dtFim.setHours(0, 0, 0, 0);
                                      if (dtInicio > dtFim) return "Não é possível informar uma data fim menor que a data início para o campo 'Data de início de vigência'";
                                    }
                                  }
                                }

                                isValidatingDtVigencia = false;
                              }}
            />
          </GridItem>

          <GridItem>
            <ReportSectionTitle divider>Filtrar por data de vencimento:</ReportSectionTitle>
          </GridItem>
          <GridItem sm={6}>
            <ReportDatePicker name="dtVencimento.dtInicio" label="Início"
                              validate={(value) => {
                                if (!isValidatingDtVencimento) {
                                  isValidatingDtVencimento = true;
                                  validateField("dtVencimento.dtFim");
                                }

                                let dtInicio = value;
                                if (Boolean(dtInicio)) {
                                  if (!dtInicio.isValid()) return "Favor informar uma data válida";

                                  let dtFim = getIn(values, "dtVencimento.dtFim");
                                  if (Boolean(dtFim)) {
                                    let rangeValido = dtInicio.isValid() && dtFim.isValid();
                                    if (rangeValido) {
                                      dtInicio = dtInicio.toDate();
                                      dtInicio.setHours(0, 0, 0, 0);
                                      dtFim = dtFim.toDate();
                                      dtFim.setHours(0, 0, 0, 0);
                                      if (dtInicio > dtFim) return "Não é possível informar uma data início maior que a data fim para o campo 'Data de vencimento'";
                                    }
                                  }
                                }

                                isValidatingDtVencimento = false;
                              }}
            />
          </GridItem>
          <GridItem sm={6}>
            <ReportDatePicker name="dtVencimento.dtFim" label="Fim"
                              validate={(value) => {
                                if (!isValidatingDtVencimento) {
                                  isValidatingDtVencimento = true;
                                  validateField("dtVencimento.dtInicio");
                                }

                                let dtFim = value;
                                if (Boolean(dtFim)) {
                                  if (!dtFim.isValid()) return "Favor informar uma data válida";

                                  let dtInicio = getIn(values, "dtVencimento.dtInicio");
                                  if (Boolean(dtInicio)) {
                                    let rangeValido = dtInicio.isValid() && dtFim.isValid();
                                    if (rangeValido) {
                                      dtInicio = dtInicio.toDate();
                                      dtInicio.setHours(0, 0, 0, 0);
                                      dtFim = dtFim.toDate();
                                      dtFim.setHours(0, 0, 0, 0);
                                      if (dtInicio > dtFim) return "Não é possível informar uma data fim menor que a data início para o campo 'Data de vencimento'";
                                    }
                                  }
                                }

                                isValidatingDtVencimento = false;
                              }}
            />
          </GridItem>
        </>

        : null
      }

      {reportByCepe && (
        <>
          <GridItem>
            <ReportSectionTitle divider>Filtros de relatório de CEPEs:</ReportSectionTitle>
          </GridItem>
          <GridItem>
            <ReportSectionTitle>Filtrar CEPE por data de vencimento:</ReportSectionTitle>
          </GridItem>
          <GridItem sm={6}>
            <ReportDatePicker name="dtVencimento.dtInicio" label="Início" />
          </GridItem>
          <GridItem sm={6}>
            <ReportDatePicker name="dtVencimento.dtFim" label="Fim" />
          </GridItem>

          <GridItem>
            <ReportSectionTitle divider />
          </GridItem>
          <GridItem sm={4}>
            <ReportSectionTitle gutterBottom>Número do CEPE:</ReportSectionTitle>
            <ReportNumberField name="nuCepe" label="Número do CEPE"
                               validate={(value) => {
                                 if (Boolean(value)) {
                                   let nuCepe = parseInt(value);
                                   if (nuCepe === 0 || nuCepe > 999_999_999) return "Número do CEPE inválido.";
                                 }
                               }}
            />
          </GridItem>

          <GridItem sm={2}/>

          <GridItem sm={4}>
            <ReportSectionTitle gutterBottom>Ano do CEPE:</ReportSectionTitle>
            <ReportNumberField name="anoCepe" label="Ano do CEPE"
                               validate={(value) => {
                                 if (Boolean(value)) {
                                   let anoCepe = parseInt(value);
                                   if (anoCepe === 0 || anoCepe > 9999) return "Ano do CEPE inválido.";
                                 }
                               }}
            />
          </GridItem>

          <GridItem>
            <ReportSectionTitle divider>Tipos de veículos:</ReportSectionTitle>
          </GridItem>
          <GridItem sm={6}>
            <ReportMultiSelect
              options={vehicleTypes}
              onSelect={handleSelectChange}
              label="Selecione tipos de veículos"
              name="tiposVeiculo"
              valueProperty="cdTipoVeiculo"
              labelProperty="deTipoVeiculo"
            />
          </GridItem>
        </>
      )}
    </GridItem>
  );
};

export default PermissionariesReport;

export function validate(values) {
  const errors = {};

  if (Boolean(values.dtVencimento.dtInicio) && !values.dtVencimento.dtInicio['_isValid']) {
    errors['dtVencimento.dtInicio'] = "Favor informar uma data válida";
  }

  if (Boolean(values.dtVencimento.dtFim) && !values.dtVencimento.dtFim['_isValid']) {
    errors['dtVencimento.dtFim'] = "Favor informar uma data válida";
  }

  if (Boolean(values.dtVigencia.dtFim) && !values.dtVigencia.dtFim['_isValid']) {
    errors['dtVigencia.dtFim'] = "Favor informar uma data válida";
  }

  return errors;
}